// Message sanitization utilities
// Removes incompatible tool calls while preserving conversation history

import { TOOL_ARCHITECTURE } from './version.js'

/**
 * Sanitize messages for current tool architecture
 * Removes incompatible tool calls and responses
 *
 * @param {Array} messages - Array of message objects
 * @param {Object} options - Sanitization options
 * @returns {Array} Sanitized messages
 */
export const sanitizeMessages = (messages, options = {}) => {
  const {
    keepUserMessages = true,
    keepAssistantText = true,
    removeToolCalls = true,
    removeToolResponses = true,
  } = options

  return messages.filter(msg => {
    // Always keep user messages
    if (msg.role === 'user' && keepUserMessages) {
      return true
    }

    // Remove tool result messages
    if (msg.role === 'tool' && removeToolResponses) {
      console.log('[Sanitizer] Removing tool response message')
      return false
    }

    // Handle assistant messages with tool calls
    if (msg.role === 'assistant') {
      if (msg.toolCalls && removeToolCalls) {
        // If there's text content, keep it without tool calls
        if (msg.content && keepAssistantText) {
          console.log('[Sanitizer] Keeping assistant text, removing tool calls')
          delete msg.toolCalls
          return true
        }

        // No text content, remove entire message
        console.log('[Sanitizer] Removing assistant message with tool calls')
        return false
      }

      // Keep assistant messages without tool calls
      return keepAssistantText
    }

    // Keep system messages
    if (msg.role === 'system') {
      return true
    }

    // Remove anything else
    return false
  })
}

/**
 * Add migration notice to conversation
 * Informs user that session has been migrated
 *
 * @param {Array} messages - Migrated messages
 * @param {string} fromVersion - Source version
 * @param {string} toVersion - Target version
 * @returns {Array} Messages with notice prepended
 */
export const addMigrationNotice = (messages, fromVersion, toVersion) => {
  const notice = {
    role: 'system',
    content: `[System Notice] Your conversation has been migrated from version ${fromVersion} to ${toVersion}. Some tool-related context may have been cleaned up for compatibility. Your conversation history is preserved.`,
  }

  return [notice, ...messages]
}

/**
 * Calculate how many messages will be removed
 * Used for logging and reporting
 *
 * @param {Array} messages - Messages to analyze
 * @returns {Object} Statistics about cleanup
 */
export const estimateCleanup = (messages) => {
  let toolCallMessages = 0
  let toolResponseMessages = 0

  messages.forEach(msg => {
    if (msg.role === 'tool') {
      toolResponseMessages++
    }
    if (msg.role === 'assistant' && msg.toolCalls) {
      toolCallMessages++
    }
  })

  return {
    total: messages.length,
    toolCallMessages,
    toolResponseMessages,
    willRemove: toolCallMessages + toolResponseMessages,
    willKeep: messages.length - (toolCallMessages + toolResponseMessages),
  }
}

/**
 * Remove tool calls from a specific message
 * Useful for selective sanitization
 *
 * @param {Object} message - Message object
 * @returns {Object} Message without tool calls
 */
export const removeToolCallsFromMessage = (message) => {
  if (message.role === 'assistant' && message.toolCalls) {
    const cleaned = { ...message }
    delete cleaned.toolCalls
    return cleaned
  }
  return message
}

/**
 * Check if a message contains incompatible tool calls
 *
 * @param {Object} message - Message object
 * @param {Array} validTools - List of valid tool names
 * @returns {boolean} True if message has incompatible tools
 */
export const hasIncompatibleTools = (message, validTools) => {
  if (message.role !== 'assistant' || !message.toolCalls) {
    return false
  }

  return message.toolCalls.some(call => !validTools.includes(call.name))
}
